import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import EditNoteIcon from "@mui/icons-material/EditNote";
import UndoIcon from "@mui/icons-material/Undo";
import { BaseTextFieldProps, InputBaseComponentProps, InputProps } from "@mui/material";
import { green, lightBlue } from "@mui/material/colors";
import FormControl from "@mui/material/FormControl";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Input from "@mui/material/Input";
import { alpha, styled } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { KeyboardKey } from "_shared/_utilities/keyboard-keys";
import Text from "_shared/text/text";
import { diffLines, diffWords } from "diff";
import { enqueueSnackbar } from "notistack";
import React, { forwardRef, JSX, useCallback, useEffect } from "react";
import CommentsButton from "work/proposals/comments/_view/comments-button";
import CommentThread from "work/proposals/comments/threads/comment-thread";
import { ProposalIssue } from "work/proposals/issues/proposal-issues";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import TextChange, { TextChangeCollection } from "work/proposals/redlining/__diff/text-change";
import DiffText from "work/proposals/redlining/__diff/view/diff-text";
import FieldRedline from "work/proposals/redlining/field-redlines/field-redline";

const RedlineTextBox = styled(TextField, {
  shouldForwardProp: (prop) => prop !== "traversable",
})<{ traversable?: boolean }>(() => ({
  minWidth: "20rem",
  "& .MuiTypography-root": {
    height: "unset",
  },
  width: "min-content",
  "& .Mui-disabled div span": {
    webkitTextFillColor: "revert",
    opacity: "0.75",
  },
}));
const ActionButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(0.5),
}));
const TextContainer = styled("div")<{ showTextInput: boolean; bold?: boolean }>(({ theme, showTextInput, bold }) => ({
  pointerEvents: showTextInput ? "none" : "auto",
  display: "flex",
  flexDirection: "column",
  height: "100%",
  paddingRight: theme.spacing(2),
  width: "100%",
  fontWeight: bold ? "500" : "normal",
}));
const InputContainer = styled("span")(() => ({
  "&.MuiInputBase-input": {
    display: "flex",
    flexDirection: "column",
    height: "unset",
  },
}));
const Changes = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  width: "100%",
}));

const ChangeContainer = styled("div")(() => ({
  alignItems: "center",
  display: "flex",
  fontSize: "0.875rem",
  width: "100%",
}));
const ResolvedValue = styled(Typography)(() => ({
  color: "inherit",
  fontWeight: "inherit",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
}));
const DiffHeader = styled(Typography)(({ theme }) => ({
  color: lightBlue[500],
  display: "inline-block",
  fontSize: "0.75rem",
  lineHeight: 1,
  minWidth: theme.spacing(13.5),
}));
const InputField = styled(Input, {
  shouldForwardProp: (prop) => prop !== "show",
})<{ show: boolean }>(({ show, theme }) => ({
  height: show ? "unset" : 0,
  margin: theme.spacing(2, 0, 1),
  width: show ? "100%" : 0,
  overflow: show ? "visible" : "hidden",
  padding: 0,
  pointerEvents: show ? "auto" : "none",
  position: show ? "relative" : "absolute",
  "& .MuiInputBase-input": {
    padding: 0,
  },
}));

interface InputSubComponentProps extends InputBaseComponentProps {
  textRedline: FieldRedline<Text>;
  issues?: ProposalIssue[];
  hideAcceptRejectButtons: boolean;
  hideUndoButton: boolean;
  readOnly?: boolean;
  hideComments?: boolean;
  commentThreads?: CommentThread[];
  focused: boolean;
  active?: boolean;
  showPlaceholder?: boolean;
  outerRef?: React.RefObject<HTMLDivElement>;
  bold?: boolean;
  animateAcceptedResolved?: boolean;
  onAcceptedResolvedAnimationStart?: () => void;
  onTraverseIn?: () => void;
  fieldConstructor: new (value: string) => Text;
  onTextFieldRedlineChange: (newRedline: FieldRedline<Text>, traversalFieldOverride?: ProposalField | null) => void;
  onActionButtonsChange?: (actionButtons: JSX.Element) => void;
  onCommentsClicked?: () => void;
  onShowTextInput?: () => void;
  onHideTextInput?: () => void;
}

const InputSubComponent = forwardRef<HTMLInputElement, InputSubComponentProps>(function InputSubComponent(
  { outerRef, ...props },
  forwardedRef
) {
  const {
    className,
    textRedline,
    issues,
    hideAcceptRejectButtons,
    hideUndoButton,
    hideComments,
    commentThreads,
    readOnly,
    required,
    focused,
    active,
    bold,
    animateAcceptedResolved,
    onAcceptedResolvedAnimationStart,
    fieldConstructor,
    onTraverseIn,
    onTextFieldRedlineChange,
    onCommentsClicked,
    onActionButtonsChange,
    onFocus,
    onBlur,
    onShowTextInput,
    onHideTextInput
  } = props as InputSubComponentProps;

  const getBaseValue = React.useCallback(() => {
    return textRedline.currentEntry?.value ?? textRedline.revisedEntry?.value ?? "";
  }, [textRedline]);

  const [draftTextValue, setDraftTextValue] = React.useState<string>(
    textRedline.currentEntry?.value ?? textRedline.revisedEntry?.value ?? ""
  );
  const [showTextInput, setShowTextInput] = React.useState<boolean>(false);

  const acceptRejectButtons = React.useMemo(() => {
    if (hideAcceptRejectButtons || textRedline.isResolved) return null;

    return (
      <>
        <Tooltip title="Accept All Changes">
          <span>
            <ActionButton
              onClick={() => {
                const newRedline = textRedline.accept();
                onAcceptedResolvedAnimationStart?.();
                onTextFieldRedlineChange(newRedline);
              }}
            >
              <CheckIcon color="success" />
            </ActionButton>
          </span>
        </Tooltip>
        <Tooltip title="Reject All Changes">
          <span>
            <ActionButton
              onClick={() => {
                const newRedline = textRedline.reject();
                onTextFieldRedlineChange(newRedline);
              }}
            >
              <CloseIcon color="error" />
            </ActionButton>
          </span>
        </Tooltip>
      </>
    );
  }, [textRedline, onTextFieldRedlineChange, hideAcceptRejectButtons, onAcceptedResolvedAnimationStart]);

  const undoButton = React.useMemo(() => {
    if (hideUndoButton || !textRedline.canBeUndone) return null;

    return (
      <Tooltip title="Undo Changes">
        <span>
          <ActionButton
            onClick={() => {
              const newRedline = textRedline.undo();
              onTextFieldRedlineChange(newRedline, newRedline.field);
            }}
          >
            <UndoIcon color="secondary" />
          </ActionButton>
        </span>
      </Tooltip>
    );
  }, [textRedline, onTextFieldRedlineChange, hideUndoButton]);

  const editButton = React.useMemo(() => {
    if (readOnly) return null;

    return (
      <Tooltip title="Edit">
        <span>
          <ActionButton
            onClick={() => {
              setDraftTextValue(getBaseValue());
              setShowTextInput(true);
              onTraverseIn?.();
              onShowTextInput?.();
            }}
          >
            <EditNoteIcon color="primary" />
          </ActionButton>
        </span>
      </Tooltip>
    );
  }, [readOnly, getBaseValue, onTraverseIn, onShowTextInput]);

  const textInputButtons = React.useMemo(() => {
    let isChanged = draftTextValue !== (textRedline?.currentEntry?.value ?? textRedline?.revisedEntry?.value ?? "");
    if (textRedline.isResolved) {
      isChanged = draftTextValue !== textRedline.currentEntry?.value;
    }
    const isSaveDisabled = !isChanged || (required && draftTextValue === "");
    return (
      <>
        <Tooltip title="Save Change">
          <span>
            <ActionButton
              color="primary"
              disabled={isSaveDisabled}
              onClick={() => {
                onTextFieldRedlineChange(textRedline.edit(new fieldConstructor(draftTextValue)), textRedline.field);
                setShowTextInput(false);
                onHideTextInput?.();
              }}
            >
              <CheckIcon />
            </ActionButton>
          </span>
        </Tooltip>
        <Tooltip title="Cancel">
          <span>
            <ActionButton
              color="inherit"
              onClick={() => {
                setDraftTextValue(textRedline.currentEntry?.value ?? textRedline.revisedEntry?.value ?? "");
                setShowTextInput(false);
                onHideTextInput?.();
              }}
            >
              <CloseIcon />
            </ActionButton>
          </span>
        </Tooltip>
      </>
    );
  }, [
    draftTextValue,
    textRedline,
    required,
    onTextFieldRedlineChange,
    fieldConstructor,
    onHideTextInput,
  ]);

  const actionButtons = React.useMemo(() => {
    return (
      <>
        {!showTextInput && !readOnly && (
          <>
            {acceptRejectButtons}
            {undoButton}
            {editButton}
          </>
        )}
        {showTextInput && textInputButtons}
        {!hideComments && (
          <CommentsButton
            toolTip="Comments"
            isActive={active}
            commentThreads={commentThreads}
            field={textRedline.field}
            onCommentsClicked={
              onCommentsClicked
                ? () => {
                  onCommentsClicked?.();
                }
                : undefined
            }
          />
        )}
      </>
    );
  }, [
    showTextInput,
    readOnly,
    textRedline,
    hideComments,
    active,
    commentThreads,
    onCommentsClicked,
    acceptRejectButtons,
    undoButton,
    editButton,
    textInputButtons
  ]);

  useEffect(() => {
    onActionButtonsChange?.(actionButtons);
  }, [onActionButtonsChange, actionButtons]);

  const cloneTextChangeCollection = useCallback((changes: TextChange[]) => {
    const collection = new TextChangeCollection();
    changes.forEach((change) => {
      collection.add(change.clone(collection));
    });
    return collection;
  }, []);

  const handleOpenTextInput = useCallback(() => {
    if (readOnly) return;
    setDraftTextValue(getBaseValue());
    setShowTextInput(true);
    onTraverseIn?.();
    onShowTextInput?.();
  }, [getBaseValue, onShowTextInput, onTraverseIn, readOnly]);

  const resolvedTextChanges = React.useMemo(() => {
    if (!textRedline.isResolved || !textRedline.textChanges?.length) return undefined;
    return textRedline.textChanges;
  }, [textRedline]);
  const showAcceptedResolvedAnimation = Boolean(
    animateAcceptedResolved &&
    textRedline.isResolved &&
    textRedline.isAccepted &&
    textRedline.wasRevised &&
    textRedline.currentEntry !== null &&
    resolvedTextChanges
  );
  const showResolvedDiff = Boolean(
    resolvedTextChanges &&
    (!textRedline.isAccepted || !textRedline.wasRevised || showAcceptedResolvedAnimation || textRedline.currentEntry === null)
  );
  const showResolvedCurrentValue = Boolean(
    textRedline.isResolved &&
    textRedline.currentEntry &&
    (!resolvedTextChanges || (textRedline.isAccepted && textRedline.wasRevised && !showAcceptedResolvedAnimation))
  );

  return (
    <TextContainer bold={bold} showTextInput={showTextInput}>
      <InputContainer className={className}>
        {!showTextInput && !textRedline.textChanges && (
          <Icon>remove</Icon>
        )}
        {!showTextInput && showResolvedDiff && resolvedTextChanges && (
          <DiffText
            onClick={handleOpenTextInput}
            textChanges={resolvedTextChanges}
            allResolved={true}
            acceptedResolved={textRedline.isAccepted && textRedline.wasRevised}
            animateAcceptedResolved={animateAcceptedResolved}
            disablePopover={true}
          />
        )}
        {!showTextInput && showResolvedCurrentValue && (
          <ResolvedValue
            onClick={handleOpenTextInput}
          >
            {textRedline.currentEntry?.value}
          </ResolvedValue>
        )}
        {!showTextInput && !textRedline.isResolved && textRedline.textChanges && (
          <DiffText
            onClick={handleOpenTextInput}
            textChanges={textRedline.textChanges}
            allResolved={textRedline.isResolved}
            onAcceptChange={(change: TextChange) => {
              try {
                const acceptedChanges = change.accept();
                let newRedline = textRedline;
                if (newRedline.textChanges === undefined) {
                  enqueueSnackbar("No text changes to accept", { variant: "warning" });
                  return;
                }
                newRedline = new FieldRedline<Text>({
                  ...newRedline,
                  textChangeCollection: cloneTextChangeCollection(acceptedChanges)
                });
                if (newRedline.textChanges?.every((change: TextChange) => change.isResolved)) {
                  const newEntry = new fieldConstructor(
                    newRedline.textChanges?.map((change: TextChange) => change.diff.value).join("")
                  );
                  newRedline = newRedline.edit(newEntry);
                  onAcceptedResolvedAnimationStart?.();
                  onTextFieldRedlineChange(newRedline);
                  return;
                }
                onTextFieldRedlineChange(newRedline, textRedline.field);
              } catch (error) {
                console.error("Error accepting change", error);
                enqueueSnackbar("Error accepting change", {
                  variant: "error",
                });
              }
            }}
            onRejectChange={(change: TextChange) => {
              try {
                const rejectedChanges = change.reject();
                let newRedline = textRedline;
                if (newRedline.textChanges === undefined) {
                  enqueueSnackbar("No text changes to reject", { variant: "warning" });
                  return;
                }
                newRedline = new FieldRedline<Text>({
                  ...newRedline,
                  textChangeCollection: cloneTextChangeCollection(rejectedChanges)
                });
                if (newRedline.textChanges?.every((change: TextChange) => change.isResolved)) {
                  const newEntry = new fieldConstructor(
                    newRedline.textChanges?.map((change: TextChange) => change.diff.value).join("")
                  );
                  newRedline = newRedline.edit(newEntry);
                  onTextFieldRedlineChange(newRedline);
                  return;
                }
                onTextFieldRedlineChange(newRedline, textRedline.field);
              } catch (error) {
                console.error("Error rejecting change", error);
                enqueueSnackbar("Error rejecting change", {
                  variant: "error",
                });
              }
            }}
            onEditChange={(change: TextChange, newValue: string) => {
              try {
                const editedChanges = change.editChange(newValue);
                let newRedline = textRedline;
                if (newRedline.textChanges === undefined) {
                  enqueueSnackbar("No text changes to edit", { variant: "warning" });
                  return;
                }
                newRedline = new FieldRedline<Text>({
                  ...newRedline,
                  textChangeCollection: cloneTextChangeCollection(editedChanges)
                });
                if (newRedline.textChanges?.every((change: TextChange) => change.isResolved)) {
                  const newEntry = new fieldConstructor(
                    newRedline.textChanges?.map((change: TextChange) => change.diff.value).join("")
                  );
                  newRedline = newRedline.edit(newEntry);
                }
                onTextFieldRedlineChange(newRedline, textRedline.field);
              } catch (error) {
                console.error("Error editing change", error);
                enqueueSnackbar("Error editing change", { variant: "error" });
              }
            }}
            disablePopover={readOnly || hideAcceptRejectButtons}
          />
        )}
        {showTextInput && (
          <>
            <Changes>
              <ChangeContainer>
                <DiffHeader variant="overline">previously:</DiffHeader>
                {textRedline.originalEntry?.value}
              </ChangeContainer>
              <ChangeContainer>
                <DiffHeader variant="overline">your changes:</DiffHeader>
                <DiffText
                  disablePopover={true}
                  textChanges={(() => {
                    const collection = new TextChangeCollection();
                    const previous = textRedline.revisedEntry?.value ?? "";
                    const next = draftTextValue;
                    const diff = textRedline.diffWords ? diffWords(previous, next) : diffLines(previous, next);
                    return diff.map((change) => new TextChange({ diff: change, parent: collection }));
                  })()}
                  allResolved={textRedline.isResolved}
                />
              </ChangeContainer>
            </Changes>
            <FormControl variant="standard" focused={focused}>
              <InputField
                multiline
                inputRef={forwardedRef}
                show={true}
                placeholder="Enter new text"
                error={issues && issues.length > 0}
                value={draftTextValue}
                onChange={(event) => {
                  try {
                    setDraftTextValue(event.target.value);
                  } catch (error) {
                    console.error("Error updating text", error);
                    enqueueSnackbar("Error updating text", { variant: "error" });
                  }
                }}
                onBlurCapture={(event: React.FocusEvent<HTMLInputElement>) => {
                  const nextFocus = event.relatedTarget as HTMLElement;
                  if (nextFocus && outerRef.current?.contains(nextFocus)) {
                    // Clicked within the same component
                    return;
                  }
                  try {
                    if (draftTextValue === getBaseValue()) return;
                    onTextFieldRedlineChange(textRedline.edit(new fieldConstructor(draftTextValue)), textRedline.field);
                    setShowTextInput(false);
                    onHideTextInput?.();
                  } catch (error) {
                    console.error("Error updating text", error);
                    enqueueSnackbar("Error updating text", {
                      variant: "error",
                    });
                  }
                }}
                onKeyDown={(event) => {
                  if (["Enter", "Tab"].includes(event.key) && draftTextValue !== "") {
                    try {
                      onTextFieldRedlineChange(textRedline.edit(new fieldConstructor(draftTextValue)), textRedline.field);
                      setShowTextInput(false);
                      onHideTextInput?.();
                    } catch (error) {
                      console.error("Error updating text", error);
                      enqueueSnackbar("Error updating text", {
                        variant: "error",
                      });
                    }
                  }
                  if (event.key === KeyboardKey.Escape) {
                    try {
                      setShowTextInput(false);
                      onHideTextInput?.();
                    } catch (error) {
                      console.error("Error updating text", error);
                      enqueueSnackbar("Error updating text", {
                        variant: "error",
                      });
                    }
                  }
                }}
                onBlur={onBlur}
                onFocus={onFocus}
              />
            </FormControl>
          </>
        )}
      </InputContainer>
    </TextContainer>
  );
});

export interface RedlineTextInputProps<T extends Text> extends BaseTextFieldProps {
  textRedline: FieldRedline<T>;
  issues?: ProposalIssue[];
  showTextInput?: boolean;
  showResolvedHelperText?: boolean;
  readOnly?: boolean;
  hideUndoButton?: boolean;
  hideAcceptRejectButtons?: boolean;
  hideLabel?: boolean;
  hideComments?: boolean;
  commentThreads?: CommentThread[];
  showPlaceholder?: boolean;
  bold?: boolean;
  onTraverseIn?: () => void;
  fieldConstructor: new (value: string) => T;
  onTextFieldRedlineChange: (newRedline: FieldRedline<Text>, traversalFieldOverride?: ProposalField | null) => void;
  onCommentsClicked?: () => void;
  onShowTextInput?: () => void;
  onHideTextInput?: () => void;
}

const TextRedlineInput = <T extends Text>(props: Readonly<RedlineTextInputProps<T>>) => {
  const {
    disabled,
    textRedline,
    issues,
    showResolvedHelperText = true,
    hideUndoButton,
    hideAcceptRejectButtons,
    readOnly,
    hideLabel,
    hideComments,
    variant,
    margin,
    commentThreads,
    showPlaceholder,
    bold,
    focused,
    fieldConstructor,
    onTextFieldRedlineChange,
    onCommentsClicked,
    onTraverseIn,
    onFocus,
    onBlur,
    onShowTextInput,
    onHideTextInput,
    ...otherProps
  } = props;

  const [actionButtons, setActionButtons] = React.useState<JSX.Element>();
  const hasAcceptedResolvedChange = textRedline.isResolved && textRedline.isAccepted && textRedline.wasRevised;
  const [animateAcceptedResolved, setAnimateAcceptedResolved] = React.useState(false);
  const helperText =
    hasAcceptedResolvedChange && showResolvedHelperText ? "Resolved" : issues?.map((entry) => entry.description).join("\n");

  const outerRef = React.useRef<HTMLDivElement>(null);
  const acceptedResolvedAnimationTimeoutRef = React.useRef<ReturnType<typeof globalThis.setTimeout> | undefined>(undefined);

  useEffect(() => {
    return () => {
      if (acceptedResolvedAnimationTimeoutRef.current !== undefined) {
        globalThis.clearTimeout(acceptedResolvedAnimationTimeoutRef.current);
      }
    };
  }, []);

  const triggerAcceptedResolvedAnimation = useCallback(() => {
    setAnimateAcceptedResolved(true);
    if (acceptedResolvedAnimationTimeoutRef.current !== undefined) {
      globalThis.clearTimeout(acceptedResolvedAnimationTimeoutRef.current);
    }
    acceptedResolvedAnimationTimeoutRef.current = globalThis.setTimeout(() => {
      setAnimateAcceptedResolved(false);
      acceptedResolvedAnimationTimeoutRef.current = undefined;
    }, 1000);
  }, []);

  const handleActionButtonsChange = useCallback((actionButtons: JSX.Element) => {
    setActionButtons(prev => (prev === actionButtons ? prev : actionButtons));
  }, []);

  const inputProps = React.useMemo<Partial<InputProps>>(() => {
    const baseProps: Partial<InputProps> = {
      inputComponent: InputSubComponent,
      inputProps: {
        readOnly,
        hideUndoButton,
        hideAcceptRejectButtons,
        textRedline,
        issues,
        hideComments,
        commentThreads,
        focused,
        showPlaceholder,
        bold,
        outerRef,
        onTraverseIn,
        onTextFieldRedlineChange,
        onCommentsClicked,
        fieldConstructor,
        onActionButtonsChange: handleActionButtonsChange,
        onFocus,
        onBlur,
        onShowTextInput,
        onHideTextInput,
        animateAcceptedResolved,
        onAcceptedResolvedAnimationStart: triggerAcceptedResolvedAnimation,
      } as InputSubComponentProps,
      endAdornment: actionButtons,
    };
    return (variant && variant !== "outlined")
      ? { ...baseProps, disableUnderline: true }
      : baseProps;
  }, [
    readOnly,
    hideUndoButton,
    hideAcceptRejectButtons,
    textRedline,
    issues,
    hideComments,
    commentThreads,
    focused,
    showPlaceholder,
    bold,
    outerRef,
    onTraverseIn,
    onTextFieldRedlineChange,
    onCommentsClicked,
    fieldConstructor,
    onFocus,
    onBlur,
    onShowTextInput,
    onHideTextInput,
    animateAcceptedResolved,
    actionButtons,
    variant,
    handleActionButtonsChange,
    triggerAcceptedResolvedAnimation,
  ]);

  return (
    <RedlineTextBox
      {...otherProps}
      disabled={disabled}
      ref={outerRef}
      focused={focused}
      traversable={Boolean(onTraverseIn)}
      error={Boolean(issues?.length)}
      helperText={helperText}
      label={hideLabel ? undefined : textRedline.label}
      variant={variant ?? "outlined"}
      margin={margin ?? "normal"}
      sx={{
        ...(hasAcceptedResolvedChange && {
          "& .MuiInputLabel-root": {
            color: green[700],
          },
          "& .MuiInputLabel-root.Mui-focused": {
            color: green[700],
          },
          "& .MuiOutlinedInput-root": {
            backgroundColor: alpha(green[500], 0.06),
          },
          "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
            borderColor: green[500],
          },
          "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: green[600],
          },
          "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: green[700],
          },
          "& .MuiFormHelperText-root": {
            color: green[700],
          },
        }),
      }}
      slotProps={{
        input: inputProps,
        inputLabel: { shrink: true },
      }}
      onFocus={onTraverseIn}
    />
  );
};

export default TextRedlineInput;
